// Import necessary hooks and libraries
import {createContext, ReactNode, use, useCallback, useEffect, useMemo, useState} from "react";
import useWebSocket, {ReadyState} from "react-use-websocket";
import {WSS_URL} from "@/lib/constants";
import {useSession} from "@/lib/auth/auth";
import {chat} from "@/lib/proto";
import {useGlobalSearchParams} from "expo-router";
import {v7 as uuidv7} from "uuid";
import {useImmerReducer} from "use-immer";
import {enableMapSet} from "immer";
import Chat = chat.Chat;
import Request = chat.Request;
import ChatResponse = chat.Response;
import ChatMessage = chat.ChatMessage;
import LoginRequest = chat.LoginRequest;
import ListChatsRequest = chat.ListChatsRequest;
import ReplayRequest = chat.ReplayRequest;
import SendChatMessageRequest = chat.SendChatMessageRequest;
import Avatar = chat.Avatar;
import MessageReadRequest = chat.MessageReadRequest;

// Create a context for chat messages
const ChatContext = createContext<ChatProviderContextProps>({
    chats: new Map(),
    chatConnected: false,
    chatBusy: false,
    chatError: null,
    unreadChatMessages: undefined,
    unreadMessagesForChat: () => {
    },
    sendChatMessage: () => {
    },
    subscribeToChat: () => {
    },
    markMessageAsRead: () => {
    },
});

type ChatProviderProps = {
    children?: ReactNode,
}

type ChatProviderContextProps = {
    chats: Map<string, ChatRecord>,
    currentChat?: ChatRecord,
    chatConnected: boolean,
    chatBusy: boolean,
    unreadChatMessages: number | undefined,
    unreadMessagesForChat: (chat_id: string) => any,
    chatError: Error | null,
    sendChatMessage: (message: SendChatMessageRequest, callback?: () => any) => any,
    subscribeToChat: (id: string) => any,
    markMessageAsRead: (chat_id: string, message_id: string) => any,
}

export type Reactions = Map<string, Avatar[]>;

interface ChatRecord {
    chat: Chat;
    messages: Map<string, ChatMessage>;
    // maps to message key
    reactions: Map<string, Reactions>;
    pendingMessages: Map<string, SendChatMessageRequest>;
}

// Define a custom hook to access the chat messages context
export function useChat() {
    const value = use(ChatContext);
    if (!value) {
        throw new Error('useChat must be wrapped in a <ChatProvider />');
    }
    return value;
}

type ChatMutation = {
    type: "response",
    msg: ChatResponse,
} | {
    type: "pendingMessage",
    msg: SendChatMessageRequest,
} | {
    type: "markAsRead",
    msg: MessageReadRequest,
};

// Define the ChatProvider component to provide chat messages context
export const ChatProvider = ({children}: ChatProviderProps) => {
    const {session} = useSession();
    const {chat_id} = useGlobalSearchParams();
    // const [chats, setChats] = useState<Map<string, ChatRecord>>(new Map());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [messageReceivedCallbacks, setMessageReceivedCallbacks] = useState<Map<string, () => any>>(new Map());

    // for Immer
    enableMapSet();

    // Initialize the WebSocket connection and retrieve necessary properties
    const {
        sendMessage: sM,
        lastMessage,
        readyState,
        getWebSocket
    } = useWebSocket(WSS_URL + "/chat/v1", {
        disableJson: true,
        onError: (event) => {
            console.warn("Chat error event: " + JSON.stringify(event));
            setError(new Error("Problemer med forbindelsen til server"))
        },
        shouldReconnect: () => true,
        queryParams: {}
    });

    const chatConnected = readyState === ReadyState.OPEN;

    const sendRequest = useCallback(
        (request: Request) => {
            setBusy(true);
            const payload = Request.encode(request).finish();
            setError(null);
            sM(payload);
            setBusy(false);
        },
        [sM],
    );

    // Configure the websocket for binary data.
    // This is not needed for web, but at least by iOS.
    useEffect(() => {
        const sock = getWebSocket() as WebSocket;
        if (sock === null) {
            return;
        }
        sock.binaryType = "blob";
    }, [getWebSocket]);

    // Handshake when initializing connection
    useEffect(() => {
        if (readyState !== ReadyState.OPEN || !session) {
            return;
        }
        console.info("Connected to chat server");
        sendRequest(Request.create({
            authenticate: LoginRequest.create({
                sessionId: session?.session_id,
            })
        }));
    }, [sendRequest, readyState, session]);

    // Holds state for the chat.
    const [chats, updateChat] = useImmerReducer(
        (chats, mutation: ChatMutation) => {
            switch (mutation.type) {
                case "response":
                    const msg = mutation.msg;
                    // console.debug("<- ChatResponse", msg);

                    if (msg.welcome) {
                        sendRequest(Request.create({
                            listChats: ListChatsRequest.create(),
                        }));
                        sendRequest(Request.create({
                            replay: ReplayRequest.create(), // todo: point in time
                        }));

                    } else if (msg.message) {
                        const message = msg.message as ChatMessage;
                        if (!message.chatId || !message.messageId || !message.sender || !message.sender.id) {
                            return;
                        }
                        // console.debug(new Date(), `Got message ${message.messageId} in chat ${message.chatId}`, message);
                        let chat = chats.get(message.chatId);
                        if (chat === undefined) {
                            console.warn(`Ignoring message`, message.messageId, `in unregistered chat`, message.chatId);
                            return;
                        }

                        // round-trip complete!
                        chat.pendingMessages.delete(message.clientMessageId!);

                        if (!!message.reaction && message.reaction.length > 0) {
                            // this is a reaction to another message
                            const inReplyTo = chat.messages.get(message.inReplyTo!);
                            if (inReplyTo === undefined) {
                                return;
                            }
                            const reactionRecords: Reactions = chat.reactions.get(inReplyTo.messageId) || new Map();
                            const reactionList = [...reactionRecords.get(message.reaction) || []];
                            const previousIndex = reactionList.findIndex(avatar => message.sender!.id === avatar.id);
                            // one more of a previous reaction of the same kind will delete it.
                            if (previousIndex !== -1) {
                                reactionList.splice(previousIndex, 1);
                            } else {
                                reactionList.push(message.sender as Avatar);
                            }
                            reactionRecords.set(message.reaction, reactionList);
                            chat.reactions.set(inReplyTo.messageId, reactionRecords);
                            chat.chat.lastActivity = message.receivedAt;
                            // console.log(chat.chat.lastActivity);
                        } else {
                            // this is a message by itself
                            chat.messages.set(message.messageId, message);
                            if (message.sender.id === session?.user_id) {
                                // mark own messages as seen immediately
                                chat.chat.lastReadMessage = message.messageId;
                            }
                            chat.chat.lastActivity = message.receivedAt;
                        }

                        const callback = messageReceivedCallbacks.get(message.clientMessageId);
                        if (callback !== undefined) {
                            // console.debug("Trigger callback for message", message.clientMessageId);
                            messageReceivedCallbacks.delete(message.clientMessageId);
                            setMessageReceivedCallbacks(new Map(messageReceivedCallbacks));
                            callback();
                        }

                    } else if (msg.chat) {
                        if (!msg.chat.id) {
                            return;
                        }
                        // console.debug("Got chat", c.id);
                        const chatRecord: ChatRecord = chats.get(msg.chat.id) || {
                            chat: Chat.create(),
                            messages: new Map(),
                            reactions: new Map(),
                            pendingMessages: new Map(),
                        };
                        chatRecord.chat = msg.chat as Chat;
                        chats.set(msg.chat.id, chatRecord);
                    } else if (msg.error) {
                        setError(new Error(msg.error.message!));
                    }
                    break;

                case "pendingMessage":
                    const chat = chats.get(mutation.msg.chatId!);
                    if (chat !== undefined) {
                        chat.pendingMessages.set(mutation.msg.clientMessageId, mutation.msg);
                    }
                    break;

                case "markAsRead":
                    const chatR = chats.get(mutation.msg.chatId);
                    if (chatR === undefined) {
                        return;
                    }
                    if (mutation.msg.messageId <= chatR.chat.lastReadMessage) {
                        return;
                    }
                    // console.log("Read message", message_id);
                    chatR.chat.lastReadMessage = mutation.msg.messageId;
                    break;

                default:
            }
        }, new Map<string, ChatRecord>()
    );

    // Handle the incoming WebSocket messages
    useEffect(() => {
        if (!lastMessage || !lastMessage.data) {
            return;
        }
        (async (lastMessage) => {
            const buf = await new Response(lastMessage.data).arrayBuffer();
            const payload = new Uint8Array(buf);
            const msg = ChatResponse.decode(payload)
            if (!msg) {
                return;
            }
            updateChat({type: "response", msg});
        })(lastMessage);
    }, [lastMessage, updateChat]);

    const sendChatMessage = useCallback(
        (message: SendChatMessageRequest, callback?: () => any) => {
            if (message.text.length + message.attachments.length + message.reaction.length === 0) {
                return;
            }
            message.clientMessageId = uuidv7();
            updateChat({type: "pendingMessage", msg: message});
            const request = Request.create({
                sendMessage: message,
            });
            if (callback !== undefined) {
                // console.debug("Registering callback for message", message.clientMessageId);
                setMessageReceivedCallbacks(
                    new Map(messageReceivedCallbacks.set(message.clientMessageId, callback))
                );
            }
            return sendRequest(request);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const currentChat = useMemo(() => {
        return chats.get(chat_id?.toString());
    }, [chat_id, chats]);

    const subscribeToChat = useCallback((chat_id: string) => {
        sendRequest(Request.create({
            replay: ReplayRequest.create({
                chatId: chat_id,
            })
        }))
    }, [sendRequest]);

    const unreadMessagesForChat = useCallback((chat_id: string) => {
        const record = chats.get(chat_id);
        if (record === undefined || !record.messages.entries().filter) {
            return 0;
        }
        return record.messages.entries()
            .filter(([key, _]) => key > record.chat.lastReadMessage)
            .reduce((acc: number, _: [string, ChatMessage]) => acc + 1, 0);
    }, [chats]);

    const unreadChatMessages = useMemo(() => {
        let total = 0;
        chats.forEach((record) => {
            total += unreadMessagesForChat(record.chat.id);
        })
        return total;
    }, [chats, unreadMessagesForChat])

    const markMessageAsRead = useCallback((chat_id: string, message_id: string) => {
        const messageRead = MessageReadRequest.create({
            chatId: chat_id,
            messageId: message_id,
        });
        updateChat({type: "markAsRead", msg: messageRead});
        sendRequest(Request.create({
            messageRead
        }));
    }, [sendRequest, updateChat]);

    // Render the ChatContext.Provider component and pass the necessary values
    return (
        <ChatContext.Provider value={{
            chats,
            chatConnected,
            chatBusy: busy,
            chatError: error,
            sendChatMessage,
            subscribeToChat,
            markMessageAsRead,
            unreadMessagesForChat,
            unreadChatMessages,
            currentChat,
        }}>
            {children}
        </ChatContext.Provider>
    );
};